import { useTheme } from "@emotion/react";
import { useClickOutside } from "@mantine/hooks";
import React from "react";

import { CustomerAddress } from "@megaron/crm-contracts";

import { fetchPlace } from "./fetchPlace";
import { ListedProfileFields } from "./LocationPicker";
import { PlacePrediction, useAutocomplete } from "./useAutocomplete";

type Props = {
  onChange: (value: CustomerAddress & Partial<ListedProfileFields>) => void;
  value: string;
  onValueChange: (value: string) => void;
  label?: string;
};

export const AddressSearchField: React.FC<Props & { predictionTypes?: string[] }> = ({
  onChange,
  predictionTypes,
  value,
  onValueChange,
  label,
}) => {
  const theme = useTheme();

  const [isFocused, setIsFocused] = React.useState(false);

  const ref = useClickOutside(() => setIsFocused(false));

  const { predictions, searchPredictions } = useAutocomplete();

  const handlePredictionSelect = async (prediction: PlacePrediction) => {
    onValueChange(prediction.description);

    const place = await fetchPlace(prediction.id);

    if (!place) {
      return;
    }

    onChange(place);
    setIsFocused(false);
  };

  return (
    <div
      css={{ display: "flex", flexDirection: "column", position: "relative" }}
      onFocus={() => setIsFocused(true)}
      ref={ref}
    >
      <span
        css={{
          fontFamily: theme.displayFontFamily,
          fontSize: "14px",
          color: theme.colors.primary,
          marginBottom: "4px",
        }}
      >
        {label || "Wyszukiwanie adresu"}
      </span>

      <div css={{ position: "relative", display: "flex", alignItems: "center" }}>
        <input
          id="autocomplete"
          type="text"
          value={value}
          onChange={(e) => {
            onValueChange(e.target.value);
            searchPredictions(e.target.value, { includedTypes: predictionTypes });
          }}
          placeholder="Szukaj adresu..."
          css={{
            width: "100%",
            border: "none",
            padding: "0.5rem 0.75rem",
            fontSize: "1rem",
            borderRadius: theme.smallBorderRadius,
            backgroundColor: theme.colors.primaryLight,
            ":focus": {
              outline: `1px solid ${theme.colors.primary}`,
            },
          }}
        />
      </div>

      {predictions.length > 0 && isFocused && (
        <ul
          css={{
            listStyleType: "none",
            padding: 0,
            backgroundColor: theme.colors.primaryLight,
            borderRadius: theme.smallBorderRadius,
            border: `1px solid ${theme.colors.border}`,
            marginTop: "4px",
            width: "100%",
            position: "absolute",
            top: "62px",
            zIndex: 1,
            overflow: "hidden",
          }}
        >
          {predictions.map((prediction) => (
            <li key={prediction.id}>
              <button
                onClick={() => handlePredictionSelect(prediction)}
                css={{
                  padding: "8px",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: theme.colors.primary,
                    color: "#fff",
                  },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  border: "none",
                  background: "none",
                  width: "100%",
                  textAlign: "left",
                }}
              >
                {prediction.description}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
